package com.schoolconnect.app.repository.common

import com.schoolconnect.app.util.scLog
import kotlin.time.Clock
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

internal data class CachedValue<T>(
    val value: T,
    val fetchedAtEpochMs: Long
)

/** Small bounded-by-key in-memory cache used by route repositories. */
internal class RepositoryCache<T> {
    private companion object { const val MAX_ENTRIES = 64 }
    private val values = LinkedHashMap<String, CachedValue<T>>()

    fun clear() = values.clear()

    private fun put(key: String, value: T) {
        if (values.size >= MAX_ENTRIES && key !in values) values.entries.firstOrNull()?.key?.let(values::remove)
        values[key] = CachedValue(value, Clock.System.now().toEpochMilliseconds())
    }

    suspend fun load(
        scope: CoroutineScope,
        tag: String,
        operation: String,
        key: String,
        forceRemote: Boolean = false,
        fetch: suspend () -> Result<T>
    ): Result<T> {
        val cached = values[key]
        if (!forceRemote && cached != null) {
            return returnCachedAndRefresh(
                scope = scope,
                tag = tag,
                operation = operation,
                context = key,
                cached = cached.value,
                refresh = {
                    fetch().onSuccess { put(key, it) }
                }
            )
        }
        return fetch().onSuccess {
            if (values.size >= MAX_ENTRIES) values.entries.firstOrNull()?.key?.let(values::remove)
            put(key, it)
        }
    }
}

private const val MAX_BACKGROUND_REFRESHES = 64
private val refreshJobsMutex = Mutex()
private val refreshJobs = LinkedHashMap<String, Job>()

/**
 * Returns the cached value immediately and refreshes the same query in the
 * background. Use this for paged/list caches whose cache entry is not a
 * [CachedValue]. The refresh is deduplicated across repository instances and
 * its successful result must update the repository cache through [refresh].
 */
internal suspend fun <T> returnCachedAndRefresh(
    scope: CoroutineScope,
    tag: String,
    operation: String,
    context: String,
    cached: T,
    refresh: suspend () -> Result<T>
): Result<T> {
    scLog("$tag $operation cacheHit $context; scheduling background refresh")
    val refreshKey = "$operation|$context"
    refreshJobsMutex.withLock {
        refreshJobs.entries.removeAll { !it.value.isActive }
        if (refreshJobs.size >= MAX_BACKGROUND_REFRESHES) {
            refreshJobs.entries.firstOrNull()?.value?.cancel()
            refreshJobs.entries.firstOrNull()?.key?.let(refreshJobs::remove)
        }
        if (refreshKey !in refreshJobs) {
            refreshJobs[refreshKey] = scope.launch {
                refresh().onSuccess {
                    scLog("$tag $operation refreshSuccess $context")
                }.onFailure { throwable ->
                    scLog("$tag $operation refreshFailure $context", throwable)
                }
            }
        }
    }
    return Result.success(cached)
}

internal suspend fun <T> loadPagedItems(
    tag: String,
    operation: String,
    pageSize: Int,
    maxPages: Int,
    startContext: String,
    totalCount: (() -> Int)? = null,
    fetchPage: suspend (offset: Int) -> List<T>
): List<T> {
    val items = mutableListOf<T>()
    var offset = 0
    var pages = 0

    while (pages < maxPages) {
        val page = fetchPage(offset)
        items += page
        pages += 1
        if (totalCount != null) {
            val expected = totalCount()
            check(expected >= 0 && items.size <= expected) { "Invalid pagination count." }
            if (items.size == expected) break
            check(page.isNotEmpty()) { "Student list ended before all records were loaded. Please refresh." }
            check(pages < maxPages) { "Roster exceeds the supported load limit. Please contact support." }
        } else if (page.size < pageSize) {
            break
        }
        offset += page.size
    }

    scLog("$tag $operation complete $startContext total=${items.size} pages=$pages")
    return items
}

internal suspend fun <T> loadRefreshableCachedValue(
    scope: CoroutineScope,
    tag: String,
    operation: String,
    context: String,
    cachedValue: CachedValue<T>?,
    fetchValue: suspend () -> Result<T>,
    updateCache: (T, Long) -> Unit,
    successDetails: (T) -> String,
    refreshSuccessDetails: (T) -> String = successDetails,
    forceRemote: Boolean = false
): Result<T> {
    if (!forceRemote) cachedValue?.let { cache ->
        scLog("$tag $operation cacheHit $context fetchedAt=${cache.fetchedAtEpochMs}")
        val refreshKey = "$operation|$context"
        refreshJobsMutex.withLock {
            refreshJobs.entries.removeAll { !it.value.isActive }
            if (refreshJobs.size >= MAX_BACKGROUND_REFRESHES) {
                refreshJobs.entries.firstOrNull()?.value?.cancel()
                refreshJobs.entries.firstOrNull()?.key?.let(refreshJobs::remove)
            }
            if (refreshKey !in refreshJobs) {
                refreshJobs[refreshKey] = scope.launch {
                    fetchValue().also { result ->
                        result.onSuccess { value ->
                            val fetchedAt = Clock.System.now().toEpochMilliseconds()
                            updateCache(value, fetchedAt)
                            scLog("$tag $operation refreshSuccess $context ${refreshSuccessDetails(value)}")
                        }
                        result.onFailure { throwable ->
                            scLog("$tag $operation refreshFailure $context", throwable)
                        }
                    }
                }
            }
        }
        return Result.success(cache.value)
    }

    scLog("$tag $operation start $context")
    return fetchValue().also { result ->
        result.onSuccess { value ->
            val fetchedAt = Clock.System.now().toEpochMilliseconds()
            updateCache(value, fetchedAt)
            scLog("$tag $operation success $context ${successDetails(value)}")
        }
        result.onFailure { throwable ->
            scLog("$tag $operation failure $context", throwable)
        }
    }
}
